import { getUserData } from "@/auth/getUserData";
import useReplies from "@/composables/v2.1/useReplies";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import useAssignChat from "@/composables/v3/useAssignChat";
import useChatInput from "@/composables/v3/useChatInput";
import useTemplates from "@/composables/v3/useTemplates";
import { useUploadAttachmentsStore } from "@/store/useUploadAttachmentsStore";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { InboxModuleType } from "@/types/inboxModule.type";
import { isChatAssignedToMeV21, shouldAssignV21 } from "@/utils/chats";
import { scrollToBottomInChatLog } from "@/views/modules/messaging-inbox/useChat";
import { Attachment, SendMessage, UploadDto, User } from "@saysimple-platform/dtos";
import { nextTick } from "@vue/composition-api";

// @ts-ignore
export default function useSendMessage() {
    const chatsStore = useChatsStore();
    const channelsStore = useChannelsStore();
    const { resetInput } = useChatInput();

    const sendMessage = (): void => {
        // Current/old flow:
        // - unarchive mits in archief
        // - assign to me/team
        // - tmp entry opgetuigd zodat de frontend dat meteen kan parsen
        // - metadata wordt gevuld, eventueel met reply entry.
        // - input/files wordt gecleared
        // - wordt naar bottom gescrolt in het chat log
        // - request naar /messages/send wordt gedaan met een "saysimple message interface"
        // - "read" wordt geset (waarschijnlijk om te zorgen dat andere agents geen unread krijgen als readForAll aanstaat)
        // - tmp entry wordt geupdate met de response payload (die ik je net liet zien), dus met id, status, external reference en time
        // - "stored messages" (bericht dat in local storage wordt opgeslagen) wordt gecleared
        // - Typing indicator wordt geunset
        // - Een websocket bericht wordt verstuurd
        // - Last entry in de chats list wordt geupdate

        if (
            !isChatAssignedToMeV21(chatsStore.activeChat.chat.assignedUserId) &&
            shouldAssignV21(channelsStore.activeChatChannel)
        ) {
            return;
        }

        const { isSendPending, hasInput, message, getFinalMessageToSend } = useChatInput();

        // When there is nothing (message or attachment) to send or we expect to send messages in the active chat only.
        if (!hasInput.value || !chatsStore.activeChat || !channelsStore.activeChatChannel) {
            return;
        }

        isSendPending.value = true;

        //----------------------------------------------------------------------

        const { assignToMe } = useAssignChat();
        const assignee = assignToMe(chatsStore.activeChat.chat, {
            executeBackendRequest: false,
        });

        //----------------------------------------------------------------------

        const { activeReply, removeReply } = useReplies();
        const { selectedTemplate, isTemplate } = useTemplates();

        //----------------------------------------------------------------------

        const sendMessage: SendMessage = {
            chat: {
                id: chatsStore.activeChat.chat.id,
                status: "open",
                assignedUserId: assignee.user?.id,
                assignedTeamId: assignee.team?.id,
            },
            content: {
                text: getFinalMessageToSend(),
                components: selectedTemplate.value.components,
                template: selectedTemplate.value?.id ? { id: selectedTemplate.value.id } : isTemplate.value,
            },
            channelActor: {
                channelId: channelsStore.activeChatChannel.id,
                user: {
                    id: getUserData().id,
                } as User,
            },
            metadata: {
                replyToEntryId: activeReply.value?.id,
            },
            contactActor: {
                identity: chatsStore.activeChat.chat.identity,
            },
        };

        //----------------------------------------------------------------------

        const { attachments } = useUploadAttachmentsStore();
        if (attachments.length > 0) {
            sendMessage.content.attachments = [];

            attachments.forEach((attachment: UploadDto) => {
                sendMessage.content.attachments?.push({
                    type: "saysimple-s3",
                    payload: `${process.env.VUE_APP_FRONTEND_ASSET_ENDPOINT}/${attachment.filePath}/${attachment.fileName}`,
                    contentType: attachment.contentType,
                    originalName: attachment.originalName,
                    filePath: attachment.filePath,
                    fileName: attachment.fileName,
                } as Attachment);
            });
        }

        //----------------------------------------------------------------------

        // @notice you'll also find the backend request in chatsStore, because we like to mutate the store there.
        // based on the API request results.
        // @todo catch exception to show notification.
        chatsStore.sendMessage(chatsStore.activeChat.chat.id, sendMessage).catch(() => null);

        //----------------------------------------------------------------------

        const { removeStoredMessage } = useStoredMessages();
        removeStoredMessage({
            chatId: chatsStore.activeChat.chat.id,
            inboxModuleType: InboxModuleType.MESSAGING,
        });

        removeReply(chatsStore.activeChat.chat.id);

        resetInput(); // will clear message text, attachments, templates, replies

        //------------------------------------------------------------------

        nextTick(scrollToBottomInChatLog);

        isSendPending.value = false;
    };

    return {
        sendMessage,
    };
}
